<script setup lang="ts">
import { watch, ref } from "vue";

import { SiteContext } from "@/app/control/programming/contexts/site-context";
import {
  TVueDailyProgram,
  IInsertRange,
  TRangeClickEvent,
  TSelectedRange,
} from "@/components/programming/weekly-program/daily-program.type";
import { useContext } from "@/composables/context";
import {
  ECommonProgramMetaMode,
  EHeaterProgramMetaMode,
  EOnProgramMetaMode,
  SubprogramRange,
  TProgramMetaMode,
} from "@/domains/v4b/program/subprogram.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { EDay } from "@/utils/date";
import { cn } from "@/utils/libs/tailwind-utils";
import { isDefined } from "@/utils/object";
import { toTimeNumber } from "@/utils/time";
import { getModeEquivalentForNotFR } from "@/utils/translation-utils";

interface IProps {
  dailyProgram: [EDay, SubprogramRange[]];
  containerWidth: number;
  editable?: boolean;
}

interface IEmits {
  (e: "insert", params: IInsertRange): void;
  (e: "range-click", params: TRangeClickEvent): void;
}

const props = withDefaults(defineProps<IProps>(), {
  editable: true,
});
const emits = defineEmits<IEmits>();
const selectedRange = defineModel<TSelectedRange | null>();

const { t, k } = useTypedIntl();

const vueDailyProgram = ref<TVueDailyProgram>();

const state = useContext(SiteContext);

/**
 * Returns the width in px of a program. A program can be scheduled from 00:00 to 23:59
 * @param range program range whose duration determines the rendered width
 */
function getRangeWidth(range: SubprogramRange): number {
  return ((toTimeNumber(range.endTime) - toTimeNumber(range.startTime)) * props.containerWidth) / 24;
}

function handleRangeClick(e: Event, range: SubprogramRange, index: number) {
  if (!props.editable) return;

  if (vueDailyProgram.value)
    emits("range-click", { event: e, selectedRange: { day: vueDailyProgram.value.day, range, index } });
}

function handleInsertProg(event: Event, range: SubprogramRange, index: number, position: "start" | "end") {
  if (!props.editable) return;

  event.stopImmediatePropagation();
  event.preventDefault();

  if (vueDailyProgram.value) emits("insert", { day: vueDailyProgram.value.day, range, index, position });
}

function getRangeInteractionHandlers(range: SubprogramRange, index: number) {
  if (!props.editable) return {};

  return {
    click: (event: Event) => handleRangeClick(event, range, index),
    keydown: (event: KeyboardEvent) => {
      if (event.key === "Enter") handleRangeClick(event, range, index);
    },
  };
}

function getRangeTitle(range: SubprogramRange): string {
  const metaMode = t(k.global.program.metaMode[range.metaMode]);
  return `${metaMode} (${range.startTime} - ${range.endTime})`;
}

function getRangeModeLabel(range: SubprogramRange): string {
  if (range.metaMode === ECommonProgramMetaMode.TEMP) return t(k.control.programming.range.temperatureMode);

  return t(k.global.program.metaMode[range.metaMode]);
}

function getRangeTemperature(range: SubprogramRange): number | undefined | null {
  if (range.metaMode !== ECommonProgramMetaMode.TEMP) return;

  return range.temperature;
}

watch(
  () => props.dailyProgram,
  ([day, newProgram]) => {
    if (!day || !newProgram) return;

    vueDailyProgram.value = {
      day: day,
      programRanges: newProgram,
    };
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <ul
    v-if="vueDailyProgram"
    class="daily-program flex w-full h-full"
  >
    <li
      v-for="(range, index) of vueDailyProgram.programRanges"
      :key="index"
      class="relative flex h-12"
      :style="{
        width: `${getRangeWidth(range)}px`,
      }"
    >
      <div
        v-if="props.editable && index === 0"
        class="absolute bg-transparent top-0 left-0 w-4 h-full cursor-pointer group z-10"
        data-test-id="insert-program-before-range-button"
        @click="handleInsertProg($event, range, index, 'start')"
      >
        <div class="relative opacity-0 group-hover:opacity-100">
          <i
            class="absolute top-[15px] left-[-10px] pi pi-plus bg-brand w-fit h-fit p-1 rounded-md text-white !text-[10px]"
          />
        </div>
      </div>

      <div
        v-tooltip.top="getModeEquivalentForNotFR(range.metaMode as TProgramMetaMode, state.siteDetails?.address)"
        :class="
          cn(
            '@container flex h-12 w-full min-w-0 flex-col items-start justify-center overflow-hidden rounded-lg border px-2 py-[6px]',
            {
              'cursor-pointer hover-selected': props.editable,
              'border-programming-comfort bg-programming-comfort-light text-programming-comfort-foreground':
                range.metaMode === EHeaterProgramMetaMode.COMFORT,
              'border-programming-eco bg-programming-eco-light text-programming-eco-foreground':
                range.metaMode === EHeaterProgramMetaMode.ECO,
              'border-programming-temperature bg-programming-temperature-light text-programming-temperature-foreground':
                range.metaMode === ECommonProgramMetaMode.TEMP,
              'border-programming-no-frost-foreground bg-programming-no-frost-light text-programming-no-frost-foreground':
                range.metaMode === EHeaterProgramMetaMode.NO_FROST,
              'border-programming-stop bg-inverse text-programming-stop-foreground':
                range.metaMode === ECommonProgramMetaMode.OFF,
              'border-programming-on bg-programming-on-light text-programming-on-foreground':
                range.metaMode === EOnProgramMetaMode.ON,
              selected: props.editable && index === selectedRange?.index && vueDailyProgram.day === selectedRange.day,
            },
          )
        "
        :data-test-id="`daily-range-${index + 1}`"
        :role="props.editable ? 'button' : undefined"
        :tabindex="props.editable ? 0 : undefined"
        :title="getRangeTitle(range)"
        v-on="getRangeInteractionHandlers(range, index)"
      >
        <div class="flex w-full min-w-0 flex-col items-start gap-0.5 leading-normal">
          <span
            class="w-full truncate whitespace-nowrap text-[10px] font-medium"
            data-test-id="daily-range-mode"
          >
            {{ getRangeModeLabel(range) }}
          </span>
          <span
            v-if="isDefined(getRangeTemperature(range))"
            class="w-full truncate whitespace-nowrap text-xs font-semibold"
            data-test-id="daily-range-temperature"
          >
            {{ getRangeTemperature(range) }}°
          </span>
        </div>
      </div>

      <div
        v-if="props.editable && index < vueDailyProgram.programRanges.length - 1"
        class="group absolute right-0 top-0 z-10 h-full w-6 translate-x-1/2"
        data-test-id="insert-program-between-ranges-button"
        @click="handleInsertProg($event, range, index, 'end')"
      >
        <div class="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-brand opacity-0 group-hover:opacity-100" />

        <div class="absolute top-[-12px] left-[2px] z-10 opacity-0 group-hover:opacity-100 cursor-pointer">
          <i class="pi pi-plus bg-brand p-1 rounded-md text-white !text-[10px]" />
        </div>

        <div class="absolute left-[-16px] top-full z-10 mt-1 hidden rounded-md bg-black/50 px-1 group-hover:block">
          <span class="text-white">{{ range.endTime }}</span>
        </div>

        <div
          class="absolute w-[24px] h-full bg-transparent top-0 right-[-9px] z-10 cursor-pointer opacity-0 group-hover:opacity-100"
        />
      </div>

      <div
        v-if="props.editable && index === vueDailyProgram.programRanges.length - 1"
        class="absolute bg-transparent top-0 right-0 w-4 h-full cursor-pointer group"
        data-test-id="insert-program-after-ranges-button"
        @click.prevent="handleInsertProg($event, range, index, 'end')"
      >
        <div class="relative opacity-0 group-hover:opacity-100">
          <i
            class="absolute top-[15px] right-[-8px] pi pi-plus bg-brand w-fit h-fit p-1 rounded-md text-white !text-[10px]"
          />
        </div>
      </div>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
.selected {
  @apply border-2 border-blue-400;
}

.hover-selected {
  @apply hover:border-2 hover:border-blue-400;
}
</style>
